import {
  adminAndOwnerOnly,
  adminOnly,
  adminOwnerAndStaffOnly,
  allAdminUsers,
} from "@utils/auth-utils";
import {
  EDIT_BLOCKED_DOMAIN,
  EDIT_CATEGORY,
  EDIT_CUSTOMER_GROUP,
  EDIT_LOCATION,
  EDIT_NOTIFICATION_TEMPLATE,
  EDIT_ORDER,
  EDIT_ORDER_STATUS,
  EDIT_PAYMENT_GATEWAY,
  EDIT_PAYMENT_METHOD,
  EDIT_PRODUCT,
  EDIT_ROLE_PERMISSION,
  EDIT_SETTING,
  EDIT_SHIPPING,
  EDIT_STATIC_BLOCK,
  EDIT_TAG,
  SEARCH_BANNER,
  VIEW_BRANCH,
  VIEW_DASHBOARD,
  VIEW_DISCOUNT,
  VIEW_MEMBERSHIP,
  VIEW_ORDER,
  VIEW_PRODUCT,
  VIEW_SEARCH_TERM,
  VIEW_USER,
  VIEW_WALLET_GROUP,
} from "@utils/constants";
import { ROUTES } from "@utils/routes";

/**
 * One shape for every sidebar entry.
 *
 * Without this the array's element type is inferred per entry, so adding a
 * property to only some of them - `disabled`, say - turns `items` into a union
 * of array types, and callers can no longer call .filter() on it.
 */
type SidebarNavItem = {
  href: string;
  label: string;
  icon?: string;
  labelTransKey?: string;
  roles?: string[];
  permissions?: string[];
  /** Shown but not navigable, for sections whose content is not ready yet. */
  disabled?: boolean;
  items?: SidebarNavItem[];
};

export const siteSettings = {
  name: "Accordia Website Management Dashboard",
  description: "",
  logo: {
    url: "/logo.png",
    alt: "Accordia Website Management Dashboard",
    href: "/",
    width: 128,
    height: 40,
  },
  defaultLanguage: "en",
  author: {
    name: "Accordia Admin",
    websiteUrl: "",
    supportEmail: "",
    address: "",
  },
  headerLinks: [],
  authorizedLinks: [
    {
      href: ROUTES.PROFILE_UPDATE,
      labelTransKey: "authorized-nav-item-profile",
    },
    {
      href: ROUTES.LOGOUT,
      labelTransKey: "authorized-nav-item-logout",
    },
  ],
  currencyCode: "MYR",
  sidebarLinks: {
    admin: [
      // {
      //   href: ROUTES.DASHBOARD,
      //   label: "sidebar-nav-item-dashboard",
      //   icon: "DashboardIcon",
      //   labelTransKey: "dashboard",
      //   roles: adminOwnerAndStaffOnly,
      //   permissions: [VIEW_DASHBOARD],
      // },
      // {
      //   href: "#",
      //   label: "sidebar-nav-item-banner",
      //   icon: "BannerIcon",
      //   labelTransKey: "banners",
      //   roles: adminOwnerAndStaffOnly,
      //   permissions: [SEARCH_BANNER],
      //   items: [
      //     {
      //       href: ROUTES.MAIN_SLIDER,
      //       label: "sidebar-nav-item-main-slider",
      //     },
      //     // {
      //     //   href: ROUTES.SIDE_BANNERS,
      //     //   label: "sidebar-nav-item-side-banners",
      //     // },
      //     {
      //       href: ROUTES.POPUP_BANNERS,
      //       label: "sidebar-nav-item-popup-banners",
      //     },
      //   ],
      // },
      // {
      //   href: "#",
      //   label: "sidebar-nav-item-customers",
      //   icon: "UsersIcon",
      //   labelTransKey: "customers",
      //   roles: adminOnly,
      //   permissions: [VIEW_USER],
      //   items: [
      //     {
      //       href: ROUTES.CUSTOMER_USERS,
      //       label: "sidebar-nav-item-customers-list",
      //       permissions: [VIEW_USER],
      //     },
      //     {
      //       href: ROUTES.CUSTOMER_GROUPS,
      //       label: "sidebar-nav-item-customer-groups",
      //       permissions: [EDIT_CUSTOMER_GROUP],
      //     },
      //   ],
      // },
      {
        href: "#",
        label: "sidebar-nav-item-custom-contents",
        icon: "SquarePlusIcon",
        roles: adminOwnerAndStaffOnly,
        permissions: [EDIT_STATIC_BLOCK],
        items: [
          {
            href: ROUTES.CUSTOM_CONTENTS_BLOG,
            label: "sidebar-nav-item-custom-contents-blog",
          },
          {
            href: ROUTES.CUSTOM_CONTENTS_NEWS,
            label: "sidebar-nav-item-custom-contents-news",
          },
          // Greyed out until the website's Resources pages for them are live.
          // The routes work already, so removing `disabled` is all it takes.
          {
            href: ROUTES.CUSTOM_CONTENTS_GUIDE,
            label: "sidebar-nav-item-custom-contents-guide",
            disabled: true,
          },
          {
            href: ROUTES.CUSTOM_CONTENTS_CUSTOMER_STORY,
            label: "sidebar-nav-item-custom-contents-customer-story",
            disabled: true,
          },
          {
            href: ROUTES.CUSTOM_CONTENTS_FEATURED,
            label: "sidebar-nav-item-custom-contents-featured",
          },
        ],
      },
      {
        href: "#",
        label: "sidebar-nav-item-system-users",
        icon: "UserGearIcon",
        labelTransKey: "users",
        roles: adminOnly,
        permissions: [VIEW_USER],
        items: [
          {
            href: ROUTES.SYSTEM_USERS,
            label: "sidebar-nav-item-system-users-list",
            permissions: [VIEW_USER],
          },

          {
            href: ROUTES.ROLES,
            label: "sidebar-nav-item-roles",
            permissions: [EDIT_ROLE_PERMISSION],
          },
        ],
      },
      // {
      //   href: ROUTES.NOTIFICATION,
      //   label: "sidebar-nav-item-notification",
      //   icon: "Bell",
      //   roles: adminOwnerAndStaffOnly,
      //   permissions: [EDIT_NOTIFICATION_TEMPLATE],
      //   items: [
      //     {
      //       href: ROUTES.NOTIFICATION,
      //       label: "sidebar-nav-item-notification-templates",
      //     },
      //     // {
      //     //   href: ROUTES.SCHEDULE_NOTIFICATION,
      //     //   label: "sidebar-nav-item-schedule-notifications",
      //     // },
      //   ],
      // },
      // {
      //   href: ROUTES.SETTINGS,
      //   label: "sidebar-nav-item-settings",
      //   icon: "SettingsIcon",
      //   labelTransKey: "settings",
      //   roles: adminOwnerAndStaffOnly,
      //   permissions: [
      //     EDIT_SETTING,
      //   ],
      // },
    ] as SidebarNavItem[],
    shop: [
      {
        href: (shop: string) => `${ROUTES.DASHBOARD}${shop}`,
        label: "sidebar-nav-item-dashboard",
        icon: "DashboardIcon",
        roles: adminOwnerAndStaffOnly,
      },
      {
        href: (shop: string) => `/${shop}${ROUTES.ATTRIBUTES}`,
        label: "sidebar-nav-item-attributes",
        icon: "AttributeIcon",
        roles: adminOwnerAndStaffOnly,
      },
      {
        href: (shop: string) => `/${shop}${ROUTES.PRODUCTS}`,
        label: "sidebar-nav-item-products",
        icon: "ProductsIcon",
        roles: adminOwnerAndStaffOnly,
      },
      {
        href: (shop: string) => `/${shop}${ROUTES.ORDERS}`,
        label: "sidebar-nav-item-orders",
        icon: "OrdersIcon",
        roles: adminOwnerAndStaffOnly,
      },
      {
        href: (shop: string) => `/${shop}${ROUTES.STAFFS}`,
        label: "sidebar-nav-item-staffs",
        icon: "UsersIcon",
        roles: adminAndOwnerOnly,
      },
      {
        href: (shop: string) => `/${shop}${ROUTES.WITHDRAWS}`,
        label: "sidebar-nav-item-withdraws",
        icon: "AttributeIcon",
        roles: adminAndOwnerOnly,
      },
    ],
  },
  product: {
    placeholder: "/product-placeholder.png",
  },
  avatar: {
    placeholder: "/avatar-placeholder.svg",
  },
  product_import_sample: {
    url: "https://super-shoppe-public.s3.ap-southeast-1.amazonaws.com/imports/sample_product_import.xlsx",
  },
  user_import_sample: {
    url: "https://kkmart-ecomm.s3.ap-southeast-1.amazonaws.com/imports/sample_user_import.xlsx",
  },
  store_import_sample: {
    url: "https://kkmart-ecomm.s3.ap-southeast-1.amazonaws.com/imports/sample_store_import.xlsx",
  },
};
